import { useMemo } from 'react';
import {
  moonAge,
  moonNameForDate,
  moonPhaseEmoji,
  tideTypeFromLunarDay,
  toLunar,
  weekdayJa,
} from '../lib/almanac.js';

interface Props {
  date: string;
}

const TIDE_TYPE_COLOR: Record<string, string> = {
  大潮: 'bg-rose-700/40 border-rose-500/50 text-rose-100',
  中潮: 'bg-sky-700/40 border-sky-500/50 text-sky-100',
  小潮: 'bg-slate-600/40 border-slate-500/50 text-slate-100',
  長潮: 'bg-amber-700/40 border-amber-500/50 text-amber-100',
  若潮: 'bg-emerald-700/40 border-emerald-500/50 text-emerald-100',
};

export function AlmanacBadges({ date }: Props): JSX.Element {
  const info = useMemo(() => {
    const wday = weekdayJa(date);
    const age = moonAge(date);
    const lunar = toLunar(date);
    const type = tideTypeFromLunarDay(lunar.day); // 旧暦日ベース
    const phaseEmoji = moonPhaseEmoji(age);
    const phaseName = moonNameForDate(date); // 旧暦日 + 翌日比較で晦を厳密化
    return { wday, age, type, lunar, phaseEmoji, phaseName };
  }, [date]);

  const tideColor = TIDE_TYPE_COLOR[info.type] ?? TIDE_TYPE_COLOR.中潮;

  // モバイル: 2 段固定（上=日付 / 下=潮名・月齢・旧暦）。
  //          潮名行が極端に長い端末でも改行位置が日付と badges の間で安定する。
  // sm 以上: 横一列（従来通り、改行は flex-wrap に任せる）。
  // 日付フォントは全デバイスで bump (text-lg → sm:text-xl) し font-bold で目立たせる。
  return (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
      <div className="font-mono text-lg sm:text-xl font-bold text-slate-100 leading-none">
        {date}
        <span className="ml-2 text-base font-normal text-slate-400">({info.wday})</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className={`px-2 py-0.5 rounded border font-bold ${tideColor}`}>
          {info.type}
        </span>
        <span className="px-2 py-0.5 rounded bg-slate-700/60 border border-slate-600/50 text-slate-200">
          <span className="mr-1 text-base align-middle">{info.phaseEmoji}</span>
          月齢 {info.age.toFixed(1)}
          <span className="mx-1 text-slate-500">·</span>
          <span className="text-amber-200">{info.phaseName}</span>
        </span>
        <span className="px-2 py-0.5 rounded bg-slate-700/60 border border-slate-600/50 text-slate-200">
          {info.lunar.display}
        </span>
      </div>
    </div>
  );
}
